import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import tripService from '../services/tripService';
import { formatDate, formatCurrency, formatDateTime } from '../utils/formatters';
import Badge from '../components/common/Badge';
import Loader from '../components/common/Loader';
import { EXPENSE_CATEGORIES } from '../utils/constants';

export default function TripDetail() {
  const { id } = useParams();
  const [trip, setTrip]   = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    tripService
      .getById(id)
      .then(setTrip)
      .catch(() => setTrip(null))
      .finally(() => setLoading(false));
  }, [id]);

  if (loading) return <Loader />;
  if (!trip) return <div className="page-content"><p>Trip not found.</p></div>;

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '1.5rem', flexWrap: 'wrap' }}>
        <Link to="/trips" className="btn btn-secondary btn-sm">← Back</Link>
        <h1 style={{ fontFamily: 'var(--font-display)', margin: 0 }}>Trip Detail</h1>
        <Badge status={trip.status} label={trip.status} />
      </div>

      <div className="grid-2" style={{ gap: '1.5rem' }}>
        <div className="card">
          <div className="card-header"><h3 style={{ margin: 0, fontFamily: 'var(--font-display)' }}>Trip Information</h3></div>
          <div className="card-body">
            {[
              ['Date', formatDate(trip.date)],
              ['Vehicle', trip.vehicle_reg],
              ['Driver', trip.driver_name],
              ['Route', trip.route || `${trip.origin} → ${trip.destination}`],
              ['Declared At', trip.declared_at ? formatDateTime(trip.declared_at) : 'Not yet declared'],
              ['Last Edited', trip.declaration_last_edited_at ? formatDateTime(trip.declaration_last_edited_at) : '—'],
              ['Notes', trip.notes || '—'],
            ].map(([k, v]) => (
              <div key={k} style={{ display: 'flex', justifyContent: 'space-between', padding: '0.5rem 0', borderBottom: '1px solid var(--gray-100)', fontSize: '0.875rem' }}>
                <span style={{ color: 'var(--gray-500)' }}>{k}</span>
                <span style={{ fontWeight: 500 }}>{v}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="card">
          <div className="card-header"><h3 style={{ margin: 0, fontFamily: 'var(--font-display)' }}>Financial Summary</h3></div>
          <div className="card-body">
            {[
              ['Revenue Declared', formatCurrency(trip.declared_amount), 'var(--brand-black)'],
              ['Total Expenses', formatCurrency(trip.total_expenses), 'var(--danger)'],
              ['Net Profit', formatCurrency(trip.profit), trip.profit >= 0 ? 'var(--success)' : 'var(--danger)'],
            ].map(([k, v, c]) => (
              <div key={k} style={{ display: 'flex', justifyContent: 'space-between', padding: '0.625rem 0', borderBottom: '1px solid var(--gray-100)' }}>
                <span style={{ fontSize: '0.875rem', color: 'var(--gray-500)' }}>{k}</span>
                <span style={{ fontFamily: 'var(--font-mono)', fontSize: '0.95rem', fontWeight: 700, color: c }}>{v}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {trip.expenses?.length > 0 && (
        <div className="card" style={{ marginTop: '1.5rem' }}>
          <div className="card-header"><h3 style={{ margin: 0, fontFamily: 'var(--font-display)' }}>Expense Breakdown</h3></div>
          <div className="card-body">
            {trip.expenses.map((e, i) => (
              <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0.5rem 0', borderBottom: '1px solid var(--gray-100)', fontSize: '0.875rem' }}>
                <div>
                  <span style={{ fontWeight: 500 }}>{EXPENSE_CATEGORIES.find(c => c.value === e.category)?.label || e.category}</span>
                  {e.description && <span style={{ color: 'var(--gray-400)', marginLeft: '0.5rem' }}>— {e.description}</span>}
                </div>
                <span style={{ fontFamily: 'var(--font-mono)', fontWeight: 600 }}>{formatCurrency(e.amount)}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {trip.declaration_audits?.length > 0 && (
        <div className="card" style={{ marginTop: '1.5rem' }}>
          <div className="card-header"><h3 style={{ margin: 0, fontFamily: 'var(--font-display)' }}>Declaration Audit Trail</h3></div>
          <div className="card-body">
            {trip.declaration_audits.map((a) => (
              <div key={a.id} style={{ padding: '0.625rem 0', borderBottom: '1px solid var(--gray-100)' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: '1rem', fontSize: '0.875rem' }}>
                  <span style={{ fontWeight: 600, textTransform: 'capitalize' }}>{a.action}</span>
                  <span style={{ color: 'var(--gray-500)' }}>{formatDateTime(a.edited_at)}</span>
                </div>
                <div style={{ fontSize: '0.8rem', color: 'var(--gray-500)', marginTop: '0.25rem' }}>
                  By: {a.edited_by_name || 'System'}
                </div>
                <div style={{ fontSize: '0.8rem', marginTop: '0.25rem' }}>
                  {a.before_expenses?.length || 0} → {a.after_expenses?.length || 0} expense items
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}